import React, { useState, useEffect } from 'react';
import { useNotification } from '../../sharedComponents/Notifications';
import ReusableTable, { TableColumn, TableAction } from '../components/adminTable';
import { submissionService, SubmissionReviewProgress, cycleService, Category, Cycle, AssessmentReviewer, ExportFormat } from '../services/adminAuth';
import { AlertCircle, User2 } from 'lucide-react';
import Pagination from '../components/Pagination';
// import ExportButton from '../../sharedComponents/ExportButton';
import XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const ReviewerAssessment: React.FC = () => {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const [submissions, setSubmissions] = useState<SubmissionReviewProgress[]>([]);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [cycles, setCycles] = useState<Cycle[]>([]);

    const [filters, setFilters] = useState({
        search: '',
        categoryId: 0,
        applicationType: 0,
        cycleId: 0
    });
    const [activeCycleId, setActiveCycleId] = useState<number>(0);
    const [isExporting, setIsExporting] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [hasInitialFetch, setHasInitialFetch] = useState(false);

    const fetchSubmissions = async (cycleIdOverride?: number) => {
        const cycleId = cycleIdOverride !== undefined ? cycleIdOverride : Number(filters.cycleId);

        if (!cycleId || cycleId === 0) {
            return;
        }

        setLoading(true);
        try {
            const response = await submissionService.getReviewAssignmentReviewProgress({
                pageNumber: currentPage,
                pageSize: pageSize,
                sortBy: 'SubmissionNumber',
                sortDescending: true,
                search: filters.search,
                categoryId: filters.categoryId,
                applicationType: 0,
                cycleId: cycleId
            });
            if (response.isSuccess && response.data) {
                setSubmissions(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch review progress'
                });
            }
        } catch (error) {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred while fetching review progress'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchDropdownData = async () => {
        try {
            const [categoriesRes, cyclesRes, activeCycleRes] = await Promise.all([
                submissionService.getAllCategories(true),
                cycleService.getAllCycles(),
                cycleService.getRunningCycle()
            ]);
            
            if (categoriesRes.isSuccess && categoriesRes.data) setCategories(categoriesRes.data);
            
            if (activeCycleRes.isSuccess && activeCycleRes.data) {
                const activeId = activeCycleRes.data.id;
                setActiveCycleId(activeId);
                setCycles([activeCycleRes.data]);
                setFilters(prev => ({ ...prev, cycleId: activeId }));
                fetchSubmissions(activeId);
                setHasInitialFetch(true);
            } else if (cyclesRes.isSuccess && cyclesRes.data) {
                setActiveCycleId(0);
                setCycles(cyclesRes.data);
                setHasInitialFetch(true);
                if (cyclesRes.data.length > 0) {
                    setFilters(prev => ({ ...prev, cycleId: cyclesRes.data[0].id }));
                }
            }

            // Handle potential individual failures in Promise.all if needed, 
            // but at least check the main ones
            if (!categoriesRes.isSuccess || !cyclesRes.isSuccess || !activeCycleRes.isSuccess) {
                const errorMessage = categoriesRes.message || cyclesRes.message || activeCycleRes.message || 'Failed to load some dropdown data';
                showNotification('warning', {
                    title: 'Warning',
                    message: errorMessage
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred while loading form data'
            });
        }
    };

    useEffect(() => {
        const init = async () => {
            await fetchDropdownData();
            setInitialized(true);
        };
        init();
    }, []);

    useEffect(() => {
        if (!initialized) return;
        if (activeCycleId > 0 && !hasInitialFetch) return;

        const handler = setTimeout(() => {
            fetchSubmissions();
        }, 300);
        return () => clearTimeout(handler);
    }, [initialized, currentPage, pageSize, filters, activeCycleId, hasInitialFetch]);

    const handleSearch = (query: string) => {
        setFilters(prev => ({ ...prev, search: query }));
        setCurrentPage(1);
    };

    const handleFilterChange = (field: string, value: any) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilters({
            search: '',
            categoryId: 0,
            applicationType: 0,
            cycleId: activeCycleId
        });
        setCurrentPage(1);
    };

    // ── Export Handlers ────────────────────────────────────────────────────────

    const generatePDFFile = (data: SubmissionReviewProgress[], reportTitle: string) => {
        const doc = new jsPDF('landscape');
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text('TECHNICAL ADVISORY GROUP (TAG)', pageWidth / 2, 15, { align: 'center' });

        doc.setFontSize(12);
        doc.text(reportTitle, pageWidth / 2, 22, { align: 'center' });

        const headers = [[
            'S/N', 'Submission ID', 'Title', 'Thematic Area', 'Reviewers', 'Final Score', 'Status'
        ]];

        const bodyData = data.map((item, index) => [
            index + 1,
            item.submissionNumber || '—',
            item.submissionTitle || '—',
            item.category || '—',
            item.reviewer?.reviewerName || 'Unassigned',
            item.totalScore ?? '—',
            item.reviewer?.isSubmitted ? 'Complete' : 'Pending'
        ]);

        autoTable(doc, {
            head: headers,
            body: bodyData,
            startY: 30,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [21, 128, 61], textColor: 255 },
            margin: { top: 35 }
        });

        doc.save(`${reportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const generateExcelFile = (data: SubmissionReviewProgress[], reportTitle: string) => {
        const headers = [
            'S/N', 'Submission ID', 'Title', 'Thematic Area', 'Reviewers', 'Final Score', 'Status'
        ];

        const bodyData = data.map((item, index) => [
            index + 1,
            item.submissionNumber || '—',
            item.submissionTitle || '—',
            item.category || '—',
            item.reviewer?.reviewerName || 'Unassigned',
            item.totalScore ?? '—',
            item.reviewer?.isSubmitted ? 'Complete' : 'Pending'
        ]);

        const wsData = [
            ['TECHNICAL ADVISORY GROUP (TAG)'],
            [reportTitle],
            [],
            headers,
            ...bodyData
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(wsData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Review Progress');

        // Basic styling
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const address = XLSX.utils.encode_col(C) + "3";
            if (!worksheet[address]) continue;
            worksheet[address].s = { font: { bold: true }, fill: { fgColor: { rgb: "ECECEC" } } };
        }

        XLSX.writeFile(workbook, `${reportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleExportData = async (format: 'excel' | 'pdf', reportTitle: string) => {
        setIsExporting(true);
        Swal.fire({
            title: `Preparing ${format.toUpperCase()}`,
            text: 'Please wait...',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
        });

        try {
            const response = await submissionService.getReviewAssignmentReviewProgress({
                pageNumber: 1,
                pageSize: 1000,
                sortBy: 'SubmissionNumber',
                sortDescending: true,
                search: filters.search,
                categoryId: filters.categoryId,
                applicationType: 0,
                cycleId: filters.cycleId || activeCycleId
            });

            if (response.isSuccess && response.data?.data && response.data.data.length > 0) {
                if (format === 'excel') generateExcelFile(response.data.data, reportTitle);
                else generatePDFFile(response.data.data, reportTitle);
                Swal.fire({ icon: 'success', title: 'Export Successful', timer: 1500 });
            } else {
                Swal.fire({ icon: 'info', title: 'No Data', text: 'No records found to export.' });
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Export Failed' });
        } finally {
            setIsExporting(false);
        }
    };

    const handleSendEmail = async (format: ExportFormat, recipients: string[]) => {
        const docType = format === 2 ? 'excel' : 'pdf';
        Swal.fire({
            title: 'Sending Email',
            text: 'Please wait...',
            allowOutsideClick: false,
            didOpen: () => { Swal.showLoading(); }
        });

        try {
            await new Promise(resolve => setTimeout(resolve, 2000));
            Swal.fire({
                icon: 'success',
                title: 'Email Sent!',
                text: `Review progress report has been sent to ${recipients.length} recipient(s).`,
                timer: 2500
            });
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Failed to send email' });
        }
    };

    const getReviewerDisplay = (reviewer: AssessmentReviewer | null) => {
        if (!reviewer) {
            return (
                <div className="flex items-center gap-2 text-amber-600">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium italic">Unassigned</span>
                </div>
            );
        }
        return (
            <div className="flex items-center gap-2 text-gray-700">
                <div className="bg-gray-100 p-1 rounded-full">
                    <User2 className="w-3.5 h-3.5 text-gray-600" />
                </div>
                <span className="text-sm font-medium">{reviewer.reviewerName}</span>
            </div>
        );
    };

    const columns: TableColumn<SubmissionReviewProgress>[] = [
        {
            key: 'submissionNumber',
            label: 'Submission ID',
            render: (value) => <span className="font-bold text-gray-900 font-mono">{value}</span>
        },
        {
            key: 'submissionTitle',
            label: 'Title / Authors',
            render: (value, record) => (
                <div>
                    <div className="font-medium text-gray-900 truncate max-w-xs" title={value}>
                        {value}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-xs">
                        {record.bookAuthors?.map(a => a.name).join(', ') || ''}
                    </div>
                </div>
            )
        },
        {
            key: 'category',
            label: 'Thematic Area',
            render: (value) => <span className="text-gray-600">{value || 'N/A'}</span>
        },
        {
            key: 'reviewer',
            label: 'Reviewer',
            render: (val) => getReviewerDisplay(val)  // val is now object or null
        },
        {
            key: 'totalScore',
            label: 'Score',
            render: (value) => (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold bg-blue-50 text-blue-700">
                    {value !== null ? value.toFixed(2) : '—'}
                </span>
            )
        },
        {
            key: 'reviewer',
            label: 'Status',
            render: (val) => (
                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    val?.isSubmitted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                    {val?.isSubmitted ? 'Completed' : 'Pending'}
                </span>
            )
        }
    ];

    const actions: TableAction<SubmissionReviewProgress>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                { 
                    label: 'View Details', 
                    onClick: (row: SubmissionReviewProgress) => {
                        if (row.reviewer?.isSubmitted) {
                            navigate(`/admin/review-form/${row?.reviewer?.reviewAssignmentId}`);
                        } else {
                            Swal.fire({
                                icon: 'info',
                                title: 'Review Not Submitted',
                                text: 'This reviewer has not submitted their review yet. Please check back later once the review is complete.',
                                confirmButtonColor: '#3085d6',
                                confirmButtonText: 'OK'
                            });
                        }
                    }
                }
            ],
            title: 'Actions'
        }
    ];

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thematic Area</label>
                    <select
                        value={filters.categoryId}
                        onChange={(e) => handleFilterChange('categoryId', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                        <option value={0}>Select Thematic Area</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cycle</label>
                    <select
                        value={filters.cycleId}
                        onChange={(e) => handleFilterChange('cycleId', parseInt(e.target.value))}
                        disabled={activeCycleId > 0}
                        className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        {activeCycleId > 0 ? (
                            <option value={activeCycleId}>{cycles.find(c => c.id === activeCycleId)?.name || 'Active Cycle'}</option>
                        ) : (
                            <>
                                <option value={0}>Select Cycle</option>
                                {cycles.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </>
                        )}
                    </select>
                </div>
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Application Type</label>
                    <select
                        value={filters.applicationType}
                        onChange={(e) => handleFilterChange('applicationType', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border rounded-md text-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                        <option value={0}>Select Type</option>
                        <option value={1}>Book</option>
                        <option value={2}>Journal Article</option>
                    </select>
                </div> */}
            </div>
            <button
                onClick={handleClearFilters}
                className="mt-4 text-sm text-blue-600 hover:underline font-medium"
            >
                Clear all filters
            </button>
        </div>
    );

    return (
        <div className="space-y-6">

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 relative overflow-x-auto">
                <ReusableTable
                    title="Reviewer Assessment"
                    subTitle="Monitor and assess reviews for submissions."
                    columns={columns}
                    data={submissions}
                    actions={actions}
                    loading={loading}
                    onSearch={handleSearch}
                    showSearch={true}
                    showFilter={true}
                    filterPanel={filterPanel}
                    onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
                    isFilterOpen={isFilterOpen}
                    emptyMessage="No submissions found."
                />

                {!loading && totalRecords > 0 && (
                    <div className="px-6 py-4 border-t border-gray-100">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            pageSize={pageSize}
                            onPageSizeChange={(size) => {
                                setPageSize(size);
                                setCurrentPage(1);
                            }}
                            totalRecords={totalRecords}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewerAssessment;
